// AuthLayout.js
import { Box, Card, Typography } from "@mui/material";
import DashboardLogo from "../../assets/DashboardLogo";
import BackgroundHouses from "../../assets/houses-bg.jpg";
import Flx from "../layout/Flx";
import Htag from "../typography/Htag";
import Txt from "../typography/Txt";
import { Link } from "react-router-dom";

/**
 * Shared full-bleed background layout for auth screens (Login + Create Account).
 *
 * Props:
 *  - children: rendered inside the glass card on the right
 *  - title: card heading (e.g. "Create Account", "Borrower Portal Log In")
 *  - description: optional subtitle below the heading
 *  - cardWidth: optional max-width override for the card (default 480)
 *  - hideHeroText: if true, hides the left-side hero text (useful for success states)
 */

const AuthLayout = ({
  children,
  title,
  description,
  cardWidth = 480,
  hideHeroText = false,
}) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "auto",
        backgroundImage: `url(${BackgroundHouses})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "fixed",
          inset: 0,
          background: "rgba(30, 58, 110, 0.65)",
          zIndex: 0,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "center" },
          justifyContent: "center",
          gap: { xs: 0, md: 6 },
          px: { xs: 2, md: 5, lg: 8 },
          py: { xs: 3, md: 4 },

          maxWidth: 1200,
          mx: "auto",
        }}
      >
        {/* Hero Text — Left Side */}
        {!hideHeroText && (
          <Box
            sx={{
              flex: { xs: "none", md: 1 },
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              justifyContent: "center",
              pr: { md: 5 },
            }}
          >
            <Htag
              component="h1"
              sx={{
                color: "white",
                fontSize: { md: "44px", lg: "54px" },
                fontWeight: 700,
                lineHeight: 1.08,
                mb: 2,
                mt: 0,
                textShadow: "0 2px 12px rgba(0,0,0,0.25)",
              }}
            >
              Reliable. Fast. Efficient.
            </Htag>

            <Box
              sx={{
                width: 48,
                height: 3,
                borderRadius: 2,
                background: "rgba(255, 255, 255, 0.4)",
                mb: 2,
              }}
            />

            <Typography
              component="p"
              sx={{
                color: "rgba(255, 255, 255, 0.88)",
                fontSize: "16px",
                lineHeight: 1.65,
                maxWidth: 440,
                textShadow: "0 1px 6px rgba(0, 0, 0, 0.72)",
              }}
            >
              Private real estate lending made simple through comprehensive loan
              programs and best-in-class execution.
            </Typography>
          </Box>
        )}

        {/* Glass Card — Right Side */}
        <Box
          sx={{
            flex: { xs: 1, md: "none" },
            width: { xs: "100%", md: cardWidth },
            maxWidth: cardWidth + 40,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Card
            elevation={0}
            sx={{
              width: "100%",
              p: { xs: 3, md: 4 },
              py: { xs: 4, md: 5 },
              //   ...cardPadding,
              borderRadius: "20px",
              background: "rgba(255, 255, 255, 0.88)",
              backdropFilter: "blur(24px) saturate(1.4)",
              WebkitBackdropFilter: "blur(24px) saturate(1.4)",
              border: "1px solid rgba(255, 255, 255, 0.45)",
              boxShadow:
                "0 16px 48px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.1)",
            }}
          >
            <Flx fw center column sx={{ mb: 3 }}>
              <DashboardLogo width="145px" />
            </Flx>

            {title && (
              <Flx center column gap={0.5} sx={{ mb: 3 }}>
                <Htag h1>{title}</Htag>
                {description && (
                  <Txt sx={{ fontSize: "13px" }} center secondary>
                    {description}
                  </Txt>
                )}
              </Flx>
            )}

            {children}
          </Card>
          <Flx center>
            <Link
              color={"primary"}
              target="_blank"
              rel="noreferrer"
              to="/terms"
              style={{
                marginTop: "12px",
                color: "#ffffff",
                fontSize: "12px",
                fontFamily: `"IBM Plex Sans", sans-serif`,
              }}
            >
              Terms of Service
            </Link>
          </Flx>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Standalone glass card on the full-bleed background (no hero text).
 * Used for success / confirmation states.
 */
export const AuthGlassCard = ({ children, maxWidth = 520 }) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "auto",
        backgroundImage: `url(${BackgroundHouses})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "fixed",
          inset: 0,
          background: "rgba(30, 58, 110, 0.65)",
          zIndex: 0,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, md: 5 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth,
            p: { xs: 3, md: 4 },
            py: { xs: 4, md: 5 },
            // ...cardPadding,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: "20px",
            background: "rgba(255, 255, 255, 0.88)",
            backdropFilter: "blur(24px) saturate(1.4)",
            WebkitBackdropFilter: "blur(24px) saturate(1.4)",
            border: "1px solid rgba(255, 255, 255, 0.45)",
            boxShadow:
              "0 16px 48px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.1)",
          }}
        >
          {children}
        </Card>
      </Box>
    </Box>
  );
};

export default AuthLayout;
